import React, { useState, useEffect } from 'react';
import { Star, ShoppingCart, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';

const ProductCard = ({ product, data_testid }) => {
  const { addToCart } = useCart();
  const [selectedSizeIdx, setSelectedSizeIdx] = useState(0);
  const [wishlisted, setWishlisted] = useState(false);

  const isSizeWise = product ? product.pricingType === 'sizewise' && product.sizes?.length > 0 : false;

  // Auto-select first in-stock size on mount / when product changes
  useEffect(() => {
    if (product && isSizeWise) {
      const firstAvail = product.sizes.findIndex(s => s.stock > 0);
      setSelectedSizeIdx(firstAvail >= 0 ? firstAvail : 0);
    }
  }, [product?._id]);

  if (!product) return null;

  const selectedSize = isSizeWise ? product.sizes[selectedSizeIdx] : null;
  const price = selectedSize ? selectedSize.price : product.price;
  const image = product.images?.[0] || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&q=80';

  // For sizewise: is the currently selected size out of stock?
  const selectedSizeOutOfStock = isSizeWise && selectedSize?.stock === 0;
  // For single SKU: product is already hidden from storefront if isActive=false, but keep a guard
  const singleOutOfStock = !isSizeWise && product.stock === 0;
  const isCartDisabled = selectedSizeOutOfStock || singleOutOfStock;

  // For sizewise: are ALL sizes out of stock?
  const allSizesOut = isSizeWise && product.sizes.every(s => s.stock === 0);

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (isCartDisabled) return;
    addToCart(product, selectedSize);
  };

  return (
    <div
      className="product-card group flex flex-col"
      data-testid={data_testid || `product-card-${product._id}`}
    >
      {/* Image */}
      <div className="relative overflow-hidden aspect-square bg-gray-100">
        <img
          src={image}
          alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 img-bakery ${allSizesOut ? 'opacity-50' : ''}`}
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&q=80'; }}
        />
        {allSizesOut && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full" data-testid={`out-of-stock-badge-${product._id}`}>
              Out of Stock
            </span>
          </div>
        )}
        {!allSizesOut && product.badge && (
          <span className="absolute top-2 left-2 bg-[#7A1125] text-white text-xs font-semibold px-2.5 py-1 rounded-full">
            {product.badge}
          </span>
        )}
        <button
          onClick={e => { e.preventDefault(); setWishlisted(!wishlisted); }}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors"
          data-testid={`wishlist-btn-${product._id}`}
        >
          <Heart size={14} fill={wishlisted ? '#7A1125' : 'none'} stroke={wishlisted ? '#7A1125' : '#1F1410'} />
        </button>
      </div>

      {/* Info */}
      <div className="p-3 flex flex-col flex-1">
        <h3 className="font-playfair font-semibold text-[#1F1410] text-sm leading-tight line-clamp-2 mb-1">
          {product.name}
        </h3>

        {/* Rating */}
        {product.rating > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <Star size={12} fill="#F59E0B" stroke="none" />
            <span className="text-xs text-[#7A6A63]">{product.rating} ({product.reviewCount})</span>
            {isSizeWise && (
              <span className="text-xs text-[#7A6A63] ml-auto">{selectedSize?.label}</span>
            )}
          </div>
        )}

        {/* Size selector — grays out 0-stock sizes */}
        {isSizeWise && (
          <div className="flex flex-wrap gap-1 mb-2">
            {product.sizes.map((s, i) => {
              const outOfStock = s.stock === 0;
              return (
                <button
                  key={s.label}
                  onClick={() => !outOfStock && setSelectedSizeIdx(i)}
                  disabled={outOfStock}
                  className={`text-xs px-2 py-0.5 rounded-full border transition-colors relative ${
                    outOfStock
                      ? 'border-[#ECE0DA] text-[#C4B8B2] bg-[#F5F0EE] cursor-not-allowed line-through'
                      : i === selectedSizeIdx
                        ? 'bg-[#7A1125] text-white border-[#7A1125]'
                        : 'border-[#ECE0DA] text-[#7A6A63] hover:border-[#7A1125]'
                  }`}
                  data-testid={`size-btn-${product._id}-${s.label}`}
                  title={outOfStock ? `${s.label} — Out of Stock` : s.label}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-auto flex items-center justify-between gap-2">
          <span className="font-playfair font-bold text-[#7A1125] text-lg">₹{price}</span>
          <button
            onClick={handleAddToCart}
            disabled={isCartDisabled}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full transition-colors duration-200 ${
              isCartDisabled
                ? 'bg-[#ECE0DA] text-[#9A8F8B] cursor-not-allowed'
                : 'bg-[#7A1125] hover:bg-[#8B1A2B] text-white'
            }`}
            data-testid={`add-to-cart-${product._id}`}
          >
            <ShoppingCart size={13} />
            {isCartDisabled ? 'Unavailable' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
